import React from "react";
import { BadgeCheck, BookOpen, Database, ExternalLink } from "lucide-react";

const STATS = [
  { value: "25+", label: "Years in PeopleSoft" },
  { value: "20+", label: "Major Engagements" },
  { value: "3+", label: "Cloud Migrations on AWS" },
  { value: "8.40–8.62", label: "PeopleTools Versions" },
];

const TRAINING = [
  { icon: Database, label: "Oracle 8i" },
  { icon: BookOpen, label: "PeopleTools" },
];

const CERTIFICATIONS = [
  {
    label: "AWS Certified Cloud Practitioner",
    issuer: "Amazon Web Services",
    image: "https://images.credly.com/size/340x340/images/00634f82-b07f-4bbd-a6bb-53de397fc3a6/image.png",
    href: "https://www.credly.com/badges/9ae13134-9b88-4185-b279-48483408eefb/public_url",
  },
  {
    label: "AWS Certified Solutions Architect – Associate",
    issuer: "Amazon Web Services",
    image: "https://images.credly.com/size/340x340/images/0e284c3f-5164-4b21-8660-0d84737941bc/image.png",
    href: "https://www.credly.com/badges/c3308cbb-3aa8-40b8-bcf5-022acd850863/linked_in_profile",
  },
  {
    label: "Microsoft Certified: Azure Fundamentals",
    issuer: "Microsoft",
    image: "https://images.credly.com/size/340x340/images/be8fcaeb-c769-4858-b567-ffaaa73ce8cf/image.png",
    href: "https://www.credly.com/badges/7b76449f-e2c0-44e5-91e9-38adcae1b17c/linked_in_profile",
  },
  {
    label: "Oracle Cloud Infrastructure 2025 Certified Architect Associate",
    issuer: "Oracle",
    image: "https://customer-assets.emergentagent.com/job_psoft-consulting/artifacts/6glvgt0x_OCI25CAA.jpg",
    imageBg: "#000000",
    href: "https://catalog-education.oracle.com/ords/certview/sharebadge?id=28796CF423D523C9B3EDC74A1A0A1D3219E5552920BC3C1D3833E914246EA221",
  },
];

export const About = () => {
  return (
    <section id="about" className="relative py-24 md:py-32" data-testid="about-section">
      <div className="container-app">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Left */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="hairline-strong w-12" />
              <span className="label-eyebrow">01 · About</span>
            </div>
            <h2
              className="font-serif text-balance"
              style={{ fontSize: "clamp(2rem, 4vw, 3.25rem)", lineHeight: 1.05, letterSpacing: "-0.01em", fontWeight: 600 }}
              data-testid="about-headline"
            >
              An advisor who bridges
              <em className="italic font-normal" style={{ color: "#901A1E" }}> functional </em>
              and
              <em className="italic font-normal" style={{ color: "#901A1E" }}> technical </em>
              domains.
            </h2>
            <p className="mt-8 text-foreground/70 leading-relaxed" data-testid="about-paragraph-1">
              Vijay Rajgopalan is an independent IT lead with a quarter century of
              hands-on Oracle PeopleSoft delivery — executing PeopleTools and
              application upgrades, architecting secure infrastructure on AWS and
              Azure, and standing up high-availability environments for state
              agencies, universities, banks and healthcare systems.
            </p>
            <p className="mt-5 text-foreground/70 leading-relaxed" data-testid="about-paragraph-2">
              Engagements are scoped to deliver outcomes, not hours. Every project
              ships with clear documentation, runbooks and a path to long-term
              self-sufficiency for the client team.
            </p>
          </div>

          {/* Right: Stats + Certs */}
          <div className="lg:col-span-7 lg:pl-8">
            <div className="grid grid-cols-2 gap-px bg-border" data-testid="about-stats">
              {STATS.map((s, i) => (
                <div key={i} className="bg-background p-8" data-testid={`stat-${i}`}>
                  <div
                    className="font-serif text-foreground"
                    style={{ fontSize: "clamp(2rem, 4vw, 3rem)", lineHeight: 1, fontWeight: 600 }}
                  >
                    {s.value}
                  </div>
                  <div className="mt-3 text-[11px] uppercase tracking-[0.22em] text-foreground/60">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-8">
              {/* Training */}
              <div>
                <div className="label-eyebrow mb-5 flex items-center gap-2">
                  <BookOpen className="w-3.5 h-3.5" />
                  Training
                </div>
                <ul className="space-y-3" data-testid="about-training">
                  {TRAINING.map((t, i) => {
                    const Icon = t.icon;
                    return (
                      <li
                        key={i}
                        className="flex items-center gap-3 border border-border bg-white px-4 py-3"
                        data-testid={`training-${i}`}
                      >
                        <Icon className="w-4 h-4 text-accent" />
                        <span className="text-sm">{t.label}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Certifications */}
              <div>
                <div className="label-eyebrow mb-5 flex items-center gap-2">
                  <BadgeCheck className="w-3.5 h-3.5" />
                  Certifications
                </div>
                <ul className="space-y-3" data-testid="about-certs">
                  {CERTIFICATIONS.map((c, i) => (
                    <li key={i}>
                      <a
                        href={c.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex items-center gap-4 border border-border bg-white p-3 hover:border-accent hover:bg-[#FBFAF6] transition-colors"
                        data-testid={`cert-${i}`}
                      >
                        {c.imageBg ? (
                          <div
                            className="w-14 h-14 flex-shrink-0 overflow-hidden flex items-center justify-center"
                            style={{ backgroundColor: c.imageBg }}
                          >
                            <img
                              src={c.image}
                              alt={`${c.label} badge`}
                              loading="lazy"
                              className="w-full h-full object-contain scale-[1.75]"
                              data-testid={`cert-image-${i}`}
                            />
                          </div>
                        ) : (
                          <img
                            src={c.image}
                            alt={`${c.label} badge`}
                            loading="lazy"
                            width="56"
                            height="56"
                            className="w-14 h-14 flex-shrink-0 object-contain"
                            data-testid={`cert-image-${i}`}
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium leading-tight">{c.label}</div>
                          <div className="text-[11px] text-foreground/55 mt-1 tracking-wide">
                            {c.issuer}
                          </div>
                        </div>
                        <ExternalLink className="w-3.5 h-3.5 text-foreground/40 group-hover:text-accent transition-colors flex-shrink-0" />
                      </a>
                    </li>
                  ))}
                </ul>
                <p className="text-[11px] text-foreground/50 mt-3" data-testid="about-credly-note">
                  Click any badge to verify
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
